/**
 * Hook for managing Dotypos employee export (push to POS)
 */

import { useState, useCallback } from 'react';
import { DotyposService } from '@/lib/integrations/dotypos-service';
import {
  PushEmployeeResult,
  PushEmployeesSummary,
  ExportMode,
  EmployeeSyncInfo,
} from '@/types/dotypos';
import { toast } from '@/hooks/ui/use-toast';

interface UseDotyposEmployeeExportReturn {
  isPushing: boolean;
  pushEmployee: (businessId: string, employeeId: string) => Promise<PushEmployeeResult | null>;
  pushEmployeesBulk: (
    businessId: string,
    employeeIds: string[],
    mode?: ExportMode
  ) => Promise<PushEmployeesSummary | null>;
  getSyncStatus: (businessId: string, employeeIds?: string[]) => Promise<EmployeeSyncInfo[]>;
  syncStatusMap: Map<string, EmployeeSyncInfo>;
}

export function useDotyposEmployeeExport(): UseDotyposEmployeeExportReturn {
  const [isPushing, setIsPushing] = useState(false);
  const [syncStatusMap, setSyncStatusMap] = useState<Map<string, EmployeeSyncInfo>>(new Map());

  const pushEmployee = useCallback(
    async (businessId: string, employeeId: string): Promise<PushEmployeeResult | null> => {
      setIsPushing(true);
      try {
        const result = await DotyposService.pushEmployee(businessId, employeeId);

        if (result.success) {
          toast.success('Employee pushed to Dotypos', {
            description: `${result.taginnEmployeeName} was ${result.action} in Dotypos`,
          });
        } else {
          toast.error('Failed to push employee', {
            description: result.error || 'Unknown error occurred',
          });
        }

        return result;
      } catch (error: any) {
        toast.error('Push failed', {
          description: error.message || 'Failed to push employee to Dotypos',
        });
        return null;
      } finally {
        setIsPushing(false);
      }
    },
    []
  );

  const pushEmployeesBulk = useCallback(
    async (
      businessId: string,
      employeeIds: string[],
      mode: ExportMode = 'sync_all'
    ): Promise<PushEmployeesSummary | null> => {
      setIsPushing(true);
      try {
        const summary = await DotyposService.pushEmployeesBulk(businessId, employeeIds, mode);

        const successCount = summary.created + summary.updated + summary.linked;
        if (successCount > 0) {
          toast.success('Employees pushed to Dotypos', {
            description: `${summary.created} created, ${summary.updated} updated, ${summary.linked} linked`,
          });
        }

        if (summary.failed > 0) {
          toast.error('Some employees failed', {
            description: `${summary.failed} employee(s) could not be pushed`,
          });
        }

        return summary;
      } catch (error: any) {
        toast.error('Bulk push failed', {
          description: error.message || 'Failed to push employees to Dotypos',
        });
        return null;
      } finally {
        setIsPushing(false);
      }
    },
    []
  );

  const getSyncStatus = useCallback(
    async (businessId: string, employeeIds?: string[]): Promise<EmployeeSyncInfo[]> => {
      try {
        const statuses = await DotyposService.getEmployeeSyncStatus(businessId, employeeIds);

        // Update the map
        const newMap = new Map(syncStatusMap);
        for (const status of statuses) {
          newMap.set(status.employeeId, status);
        }
        setSyncStatusMap(newMap);

        return statuses;
      } catch (error: any) {
        console.error('Failed to get sync status:', error);
        return [];
      }
    },
    [syncStatusMap]
  );

  return {
    isPushing,
    pushEmployee,
    pushEmployeesBulk,
    getSyncStatus,
    syncStatusMap,
  };
}
